import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  Copy,
  Save,
  CalendarPlus,
  Hash,
  Wand2,
  RefreshCw,
  ArrowRight,
  FileText,
  CheckCircle2,
  Lock,
  Library as LibraryIcon,
  Image as ImageIconLucide,
  ImagePlus,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { isContentPolicyBlockError, isContentSafetyUnavailableError, formatApiError, getActiveWorkspaceId } from "@/lib/api";
import {
  readStoredComposer,
  writeStoredComposer,
} from "@/lib/generateComposerStorage";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ImageDropzone from "@/components/ImageDropzone";
import PostPreviewCard from "@/components/PostPreviewCard";
import SchedulePostDialog from "@/components/SchedulePostDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import AILoadingState from "@/components/AILoadingState";
import BrandLearningStatusCard from "@/components/BrandLearningStatusCard";
import { ProgressRail } from "@/components/premium";
import { useAuth } from "@/lib/auth";
import usePlatformCapabilities from "@/lib/usePlatformCapabilities";

const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "X / Twitter"];
const RANDOM_TONE_LABEL = "Random";
const CUSTOM_TONE_LABEL = "Other";
const CUSTOM_TONE_MAX = 25;
// Feb-2026 post-length control. "short" was removed; legacy composer
// state / saved drafts that still carry it are coerced to "standard".
// "standard" is the default and produces today's baseline caption style
// unchanged.
const POST_LENGTHS = ["standard", "long", "detailed"];
const POST_LENGTH_LABELS = {
  standard: "Standard",
  long: "Long",
  detailed: "Detailed",
};
const POST_LENGTH_HINTS = {
  standard: "Quick, natural social post",
  long: "More context and depth",
  detailed: "Long-form social content",
};
const coercePostLength = (v) => {
  if (typeof v !== "string") return "standard";
  const k = v.trim().toLowerCase();
  return POST_LENGTHS.includes(k) ? k : "standard";
};
// Single authoritative pool for "Random" — real concrete preset tones only.
// Excludes "Use my brand tone" (special-cased upstream), and never contains
// the sentinel labels "Random" / "Other" themselves. If this list is ever
// emptied by accident, onGenerate falls back to "Friendly" so Random can
// never resolve to undefined / "" / "Random" / "Other".
const RANDOM_TONE_POOL = ["Friendly", "Professional", "Casual", "Bold", "Playful", "Luxurious", "Local / Community", "Promotional"];
const TONES = ["Use my brand tone", ...RANDOM_TONE_POOL, RANDOM_TONE_LABEL, CUSTOM_TONE_LABEL];
// Feb-2026 UX polish — a short secondary hint per tone shown inside the
// dropdown option rows so users can pick faster. Purely presentational;
// the AI pipeline still receives the exact tone STRING (unchanged).
const TONE_HINTS = {
  "Use my brand tone": "Match your saved brand voice",
  "Friendly": "Warm and approachable",
  "Professional": "Clear, polished and confident",
  "Casual": "Relaxed, everyday language",
  "Bold": "Direct and high-energy",
  "Playful": "Light, fun and a little cheeky",
  "Luxurious": "Refined and elevated",
  "Local / Community": "Neighborly and community-minded",
  "Promotional": "Sales-forward and action-oriented",
  [RANDOM_TONE_LABEL]: "Pick one at random each time",
  [CUSTOM_TONE_LABEL]: "Describe your own tone in a few words",
};

// Post-generation goals (Phase 15 simplification). Down from 19 → 9 curated
// options aligned to how small-business owners actually think about a single
// post's purpose. "Custom goal" reveals a text input so power users can
// still pass a bespoke intent. Angle/category cues (BTS, tips, etc.) are
// handled by the AI content categories, not by the top-level goal dropdown.
const CUSTOM_GOAL_VALUE = "Custom goal";
const POST_GOALS = [
  { value: "No specific goal", label: "No specific goal", hint: "Surprise me — natural & low-pressure" },
  { value: "Promote an offer", label: "Promote an offer", hint: "Only when there's a real offer to share" },
  { value: "Get more engagement", label: "Get more engagement", hint: "Ask something worth answering" },
  { value: "Drive website traffic", label: "Drive website traffic", hint: "Send readers to your site or booking" },
  { value: "Generate leads", label: "Generate leads", hint: "Invite quotes, consults, or signups" },
  { value: "Announce something", label: "Announce something", hint: "New product, event, opening, milestone" },
  { value: "Build brand awareness", label: "Build brand awareness", hint: "Who you are & why you exist" },
  { value: "Educate audience", label: "Educate audience", hint: "Teach one useful thing" },
  { value: CUSTOM_GOAL_VALUE, label: "Custom goal", hint: "Type your own goal below" },
];

// Image source options visible on the form. "library" only consumes a
// library slot (no AI image credit); "ai" consumes one AI image generation;
// "none" leaves the existing image alone and is the most common regen case.
// Feb-2026: authentic business media is prioritised over AI generation.
// Library + Upload appear first (recommended); AI + No image follow.
const IMAGE_SOURCES = [
  { id: "library", label: "Use Business Media", desc: "Pick from your photos — most authentic", Icon: LibraryIcon, recommended: true },
  { id: "upload", label: "Upload a photo", desc: "Drop or choose your own file", Icon: ImagePlus, recommended: true },
  { id: "ai", label: "Generate with AI", desc: "Counts as 1 AI image", Icon: Sparkles },
  { id: "none", label: "No image", desc: "Keep current or skip", Icon: Ban },
];

function HashtagChip({ value }) {
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(value);
        toast.success("Copied " + value);
      }}
      data-testid="hashtag-chip"
      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:border-[color:var(--border-strong)]"
    >
      {value}
    </button>
  );
}

export default function GeneratePage() {
  const { user } = useAuth();
  // ---- Topic input length policy ----
  // Frontend cap must match backend GenerateRequest.topic max_length so
  // an over-limit input is caught inline BEFORE the request. Pasted
  // text over the cap is safely truncated and a small non-blocking
  // toast tells the user their input was clipped.
  const TOPIC_MAX_LEN = 2500;
  // ---- Session-scoped composer hydration (per workspace) ----
  // Preserves unfinished composer inputs across sidebar navigation +
  // page refresh WITHIN a single logged-in browser session. Cleared on
  // logout. Never touches saved drafts. Workspace-scoped so a topic
  // typed in Workspace A never appears in Workspace B.
  //   - Preserved:      topic, goal, customGoal, tone, customTone,
  //                     targetPlatforms, imageSource
  //   - NOT preserved:  editedImage (dataURL, quota risk), editedCaption
  //                     / editedHashtags / result / currentPostId /
  //                     pendingDraft (server-owned draft state), and
  //                     ephemeral UI flags (loading / dialog open).
  // The initial read is captured ONCE (useRef) so lazy useState()
  // initializers all see the same snapshot even if sessionStorage were
  // to change mid-mount.
  const composerWorkspaceIdRef = useRef(getActiveWorkspaceId() || null);
  const hydratedComposerRef = useRef(null);
  if (hydratedComposerRef.current === null) {
    hydratedComposerRef.current = readStoredComposer(composerWorkspaceIdRef.current) || {};
  }
  const _hy = hydratedComposerRef.current;
  const [topic, setTopic] = useState(() => (typeof _hy.topic === "string" ? _hy.topic : ""));
  const [goal, setGoal] = useState(() => (typeof _hy.goal === "string" && _hy.goal ? _hy.goal : "No specific goal"));
  // Phase 15: when goal === "Custom goal" the UI reveals a small text
  // input; the resolved value sent to the backend is this string (with
  // safe fallback to "No specific goal" if empty).
  const [customGoal, setCustomGoal] = useState(() => (typeof _hy.customGoal === "string" ? _hy.customGoal : ""));
  // ``platform`` remains the *primary* platform used for backend
  // tone/format heuristics and the draft.platform field — keep it for
  // backward compatibility with the persisted-draft flow. ``targetPlatforms``
  // is the multi-select list shown in the UI; the first entry mirrors
  // ``platform``. Defaulting to ["Instagram"] preserves current behaviour
  // for users coming back to a session that only had one platform.
  const [platform, setPlatform] = useState("Instagram");
  // Phase 17c fix: previously defaulted to ["Instagram"] which caused
  // Instagram to appear preselected even when the user had no IG
  // connection or IG wasn't active on their plan. Start EMPTY and let
  // the effect below preselect only currently-active platforms.
  const [targetPlatforms, setTargetPlatforms] = useState(() => (
    Array.isArray(_hy.targetPlatforms)
      ? _hy.targetPlatforms.filter((p) => typeof p === "string")
      : []
  ));
  const [tone, setTone] = useState(() => (typeof _hy.tone === "string" && _hy.tone ? _hy.tone : "Use my brand tone"));
  // Free-text custom tone that reveals when the user picks "Other" in the
  // Tone selector. Capped at CUSTOM_TONE_MAX chars. Resolved into
  // effectiveTone at generate-time and sent as tone_override.
  const [customTone, setCustomTone] = useState(() => (
    typeof _hy.customTone === "string" ? _hy.customTone.slice(0, CUSTOM_TONE_MAX) : ""
  ));
  const [imageSource, setImageSource] = useState(() => (
    typeof _hy.imageSource === "string" && _hy.imageSource ? _hy.imageSource : "none"
  ));
  // Feb-2026 prompt-first UX: optional Content Format hint. "auto" lets
  // the backend `_detect_content_format(topic)` decide (meme / promo /
  // educational / announcement / standard). Explicit override always
  // wins over auto-detect. Users rarely need to touch this — the goal
  // is that a natural-language prompt like "make me a meme about X" is
  // enough on its own.
  const [contentFormat, setContentFormat] = useState(() => (
    typeof _hy.contentFormat === "string" && _hy.contentFormat ? _hy.contentFormat : "auto"
  ));
  // Feb-2026 post-length preference. Missing / unknown / legacy "short"
  // → "standard" via coercePostLength (backward-compatible).
  const [postLength, setPostLength] = useState(() => coercePostLength(_hy.postLength));
  const [loading, setLoading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [connections, setConnections] = useState([]);
  // Phase 17 platform-slot ctx — comes back on the same /connections
  // response so we can gate Generate to only the user's active plan
  // platforms (or all, on unlimited plans).
  const [slotCtx, setSlotCtx] = useState(null);
  // Shared platform-capability hook — the backend is the source of
  // truth for which platforms require an image (currently Instagram &
  // TikTok). When the user's target selection includes any of those,
  // we (a) disable the "No image" tile, (b) show an inline warning
  // pointing at Upload / Generate AI, and (c) auto-flip the current
  // selection off "none" so the primary CTA isn't a permanent no-op.
  const { anyRequiresImage, platformsRequiringImage, requiresImageReason } = usePlatformCapabilities();
  const imageRequiredPlatforms = useMemo(
    () => platformsRequiringImage(targetPlatforms),
    [platformsRequiringImage, targetPlatforms],
  );
  const imageIsRequired = imageRequiredPlatforms.length > 0;
  // Lightweight library status so the UI can warn proactively when the
  // user picks "Use Business Image Library" with an empty library — no
  // need to make them click Generate first.
  const [libraryCount, setLibraryCount] = useState(null);
  // Phase 15: full library list (up to MEDIA_LIBRARY_MAX_ITEMS from the
  // backend) used by the picker. Loaded lazily only when the user opens
  // the picker or when we auto-select the single image case.
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  // "auto" (backend relevance auto-picker) | "manual" (user picked one).
  const [libraryPickMode, setLibraryPickMode] = useState("auto");
  // media_id of the manually-selected image. Sent to backend as
  // `library_image_id` when set.
  const [librarySelectedImageId, setLibrarySelectedImageId] = useState(null);
  // Controls the shadcn Dialog picker visibility.
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);

  // ``result`` mirrors the AI payload (caption, hashtags, variants, CTA,
  // ideas). ``currentPostId`` is the DB id of the auto-persisted draft —
  // we update it in-place on regenerate / edit so users never end up with
  // a graveyard of stale drafts in Content Manager.
  const [result, setResult] = useState(null);
  const [editedCaption, setEditedCaption] = useState("");
  const [editedHashtags, setEditedHashtags] = useState([]);
  const [editedImage, setEditedImage] = useState(null);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [resumed, setResumed] = useState(false);

  // Resume-draft flow (shown before the user has clicked anything when a
  // saved draft already exists from a previous session).
  const [pendingDraft, setPendingDraft] = useState(null);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  // ----- Session-scoped composer persistence -----
  // Writes the current composer inputs to sessionStorage (keyed by active
  // workspace) whenever any preserved field changes. Skipped once
  // `resumed=true` — at that point the user is editing an already-saved
  // draft, so the composer state IS the draft and we don't want stale
  // pre-resume typing to overwrite it later. Cleared on logout via
  // /lib/generateComposerStorage::clearAllStoredComposers().
  useEffect(() => {
    if (resumed) return;
    writeStoredComposer(composerWorkspaceIdRef.current, {
      topic,
      goal,
      customGoal,
      tone,
      customTone,
      targetPlatforms,
      imageSource,
      postLength,
      contentFormat,
    });
  }, [resumed, topic, goal, customGoal, tone, customTone, targetPlatforms, imageSource, postLength, contentFormat]);

  // ----- AI generation cap tracking -----
  const [sub, setSub] = useState(null);
  const refreshSubscription = useCallback(async () => {
    try {
      const res = await api.get("/billing/subscription");
      setSub(res.data || null);
    } catch (_e) {
      /* silent */
    }
  }, []);
  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  // Auto-flip the "No image" selection when the user's platform picks
  // suddenly require an image. Preserves the user's existing choice if
  // they already had "library" / "ai" / "upload" — we ONLY nudge them
  // off "none". Defaults to "ai" as the most likely intent (they picked
  // Instagram so they probably want an image right now); if they'd
  // rather upload manually they can flip after the auto-nudge.
  useEffect(() => {
    if (imageIsRequired && imageSource === "none") {
      setImageSource("ai");
    }
  }, [imageIsRequired, imageSource]);

  // Fetch connection statuses once so we can lock unavailable platforms
  // in the multi-select and in the preview tabs.
  useEffect(() => {
    let live = true;
    api
      .get("/connections")
      .then((res) => {
        if (!live) return;
        setConnections(res?.data?.platforms || []);
        setSlotCtx(res?.data?.platform_slots || null);
        // Phase 17c: seed targetPlatforms with the intersection of
        //   (currently active plan slots) ∩ (currently connected)
        // so the multi-select doesn't show Instagram-selected on a
        // brand-new Starter account with no connections.
        const active = res?.data?.platform_slots?.active_platforms || [];
        const unlim = res?.data?.platform_slots?.unlimited;
        const canonToLabel = { facebook: "Facebook", instagram: "Instagram", x: "X / Twitter", linkedin: "LinkedIn" };
        // Backend returns p.platform in canonical form ("X", "Facebook",
        // "Instagram", "LinkedIn"). Convert to the same canonical keys
        // used by platform_slots so the filter below actually matches
        // — previously we compared "X / Twitter" against "X" and X was
        // silently dropped from the preselection.
        const backendToCanon = { Facebook: "facebook", Instagram: "instagram", X: "x", LinkedIn: "linkedin" };
        const plats = res?.data?.platforms || [];
        const connectedCanons = new Set(
          plats
            .filter((p) => p.connected)
            .map((p) => backendToCanon[p.platform])
            .filter(Boolean)
        );
        setTargetPlatforms((prev) => {
          if (prev.length > 0) return prev; // user already made a choice
          if (unlim) {
            // Pro/Business — preselect first connected only, not all.
            const first = plats.find((p) => p.connected && !p.awaiting_app_review && !p.coming_soon);
            const firstCanon = first ? backendToCanon[first.platform] : null;
            return firstCanon ? [canonToLabel[firstCanon] || first.platform] : [];
          }
          // Free/Starter — preselect connected+active only.
          return active
            .filter((c) => connectedCanons.has(c))
            .map((c) => canonToLabel[c] || c);
        });
      })
      .catch(() => {});
    api
      .get("/media-library")
      .then((res) => {
        if (!live) return;
        setLibraryCount(res?.data?.count ?? 0);
        setLibraryImages(Array.isArray(res?.data?.items) ? res.data.items : []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  // Phase 15: when the user has exactly ONE image in the library and picks
  // "Use Business Image Library", auto-select that image (no picker needed).
  // For 2+ images, default to "auto" (backend relevance-based picker); the
  // user can flip to manual via the picker button.
  useEffect(() => {
    if (imageSource !== "library") {
      // Reset selection state when leaving library mode so a stale choice
      // doesn't survive into a later library-mode session.
      if (librarySelectedImageId !== null) setLibrarySelectedImageId(null);
      if (libraryPickMode !== "auto") setLibraryPickMode("auto");
      return;
    }
    if (libraryImages.length === 1) {
      const only = libraryImages[0];
      const id = only?.media_id || null;
      if (id && librarySelectedImageId !== id) {
        setLibrarySelectedImageId(id);
        setLibraryPickMode("manual");
      }
    }
  }, [imageSource, libraryImages, librarySelectedImageId, libraryPickMode]);

  // Keep ``platform`` (single canonical) synced with the first item of the
  // multi-select so legacy backend fields + persisted-draft flow stay sane.
  useEffect(() => {
    if (targetPlatforms.length > 0 && targetPlatforms[0] !== platform) {
      setPlatform(targetPlatforms[0]);
    }
  }, [targetPlatforms, platform]);

  const connectionsByPlatform = useMemo(() => {
    // Backend uses canonical platform keys ("X", not "X (Twitter)"); the UI
    // labels each option with the customer-facing name. We index by the
    // canonical key here and translate on lookup.
    const map = {};
    (connections || []).forEach((c) => {
      const key = c.platform || c.name;
      if (!key) return;
      map[key] = c;
    });
    return map;
  }, [connections]);

  const canonicalKey = (label) =>
    label === "X / Twitter" || label === "X (Twitter)" ? "X" : label;
  const connFor = (label) => connectionsByPlatform[canonicalKey(label)];

  const previewAvailability = useMemo(() => {
    const pick = (label) => {
      const c = connFor(label);
      if (!c) return "not_connected";
      if (c.coming_soon) return "coming_soon";
      if (c.awaiting_app_review) return "locked";
      if (!c.connected) return "not_connected";
      return "ok";
    };
    return {
      instagram: pick("Instagram"),
      facebook: pick("Facebook"),
      x: pick("X / Twitter"),
      linkedin: pick("LinkedIn"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionsByPlatform]);

  const aiUsed = sub?.ai_generations_used || 0;
  const aiLimit = sub?.ai_generations_limit;
  const aiUnlimited = aiLimit === null || aiLimit === undefined;
  const aiRemaining = aiUnlimited ? null : Math.max(0, aiLimit - aiUsed);
  const aiAtLimit = !aiUnlimited && aiRemaining === 0;

  // Multi-platform AI generation pre-check: each selected platform
  // consumes 1 AI credit. Block generation *before* the API call if the
  // planned cost exceeds remaining credits. Backend accounting is
  // unchanged; this is a UI-side convenience mirroring the existing
  // 402 message. Regenerate paths reuse the same `aiUnitsNeeded` value.
  const aiUnitsNeeded = Math.max(1, (targetPlatforms || []).length || 1);
  const aiWouldExceed = !aiUnlimited && aiRemaining !== null && aiUnitsNeeded > aiRemaining;
  const aiCounterTone = aiAtLimit
    ? "text-[color:var(--danger)]"
    : (!aiUnlimited && aiRemaining !== null && aiRemaining <= 5
        ? "text-[color:rgb(255,190,110)]"
        : "text-[color:var(--text-3)]");
  const aiCounterLabel = aiUnlimited
    ? "Fair-use AI generations"
    : `${aiRemaining} of ${aiLimit} AI generations remaining`;

  // ----- Hydrate from existing auto-persisted draft on mount -----
  // We never auto-load the draft into the editor; we only surface a banner
  // so the user can explicitly choose Continue vs Start new. This avoids
  // surprising users who specifically came back to start over.
  useEffect(() => {
    let live = true;
    api
      .get("/posts/latest-generate-draft")
      .then((res) => {
        if (!live) return;
        const draft = res?.data?.draft || null;
        if (draft && draft.post_id) {
          setPendingDraft(draft);
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const continueDraft = (draft) => {
    setCurrentPostId(draft.post_id);
    const meta = draft.generate_meta || {};
    const aiResult = meta.result || {
      caption: draft.caption,
      hashtags: draft.hashtags,
      call_to_action: draft.cta,
      caption_variants: null,
      alt_captions: [],
      post_ideas: [],
    };
    setResult(aiResult);
    setEditedCaption(draft.caption || aiResult.caption || "");
    setEditedHashtags(draft.hashtags || aiResult.hashtags || []);
    setEditedImage(draft.image_url || null);
    setPlatform(draft.platform || "Instagram");
    if (meta.topic) setTopic(meta.topic);
    if (meta.goal) {
      // Phase 15: resume-draft may have a legacy goal string (e.g. "Quick tip",
      // "Behind the scenes") that isn't in the new 9-item curated list. Map
      // any non-matching value into "Custom goal" + preserve the original as
      // the custom text so the user sees their intent instead of getting
      // silently reset to "No specific goal".
      const known = POST_GOALS.some((g) => g.value === meta.goal);
      if (known) {
        setGoal(meta.goal);
      } else {
        setGoal(CUSTOM_GOAL_VALUE);
        setCustomGoal(meta.goal);
      }
    }
    if (meta.tone_override) {
      // Restore the Tone selector to the saved value if it's one of the
      // known preset labels; otherwise the saved override is a free-text
      // custom tone typed under "Other", so restore that state instead.
      // Random resolves to a concrete preset AT GENERATE-TIME (see
      // effectiveTone in onGenerate), so a saved override will never be
      // the literal "Random".
      const isKnownPreset =
        TONES.includes(meta.tone_override) &&
        meta.tone_override !== RANDOM_TONE_LABEL &&
        meta.tone_override !== CUSTOM_TONE_LABEL;
      if (isKnownPreset) {
        setTone(meta.tone_override);
      } else {
        setTone(CUSTOM_TONE_LABEL);
        setCustomTone(String(meta.tone_override).slice(0, CUSTOM_TONE_MAX));
      }
    }
    // Restore post length if the saved draft has one; legacy drafts
    // without the field fall through to the current selector value
    // (already "standard" by default). Legacy "short" or unknown
    // strings coerce to "standard" via coercePostLength.
    if (typeof meta.length_override === "string" && meta.length_override) {
      setPostLength(coercePostLength(meta.length_override));
    }
    setPendingDraft(null);
    setResumed(true);
    toast.success("Resumed your latest draft. Editing here will keep it saved.");
  };

  const startNewGeneration = async () => {
    // Capture the draft id BEFORE clearing local state so we can persist
    // the "dismissed" intent even after resetting the editor.
    const dismissId = pendingDraft?.post_id;
    // Clear editor state and close the confirm dialog immediately for
    // snappy UX. The dismissed draft is NOT deleted — it remains visible
    // in Content Manager. Future Generate clicks will create a fresh
    // draft.
    setResult(null);
    setCurrentPostId(null);
    setEditedCaption("");
    setEditedHashtags([]);
    setEditedImage(null);
    setPendingDraft(null);
    setResumed(false);
    setConfirmNewOpen(false);
    toast.info("Ready for a new generation. Your previous draft is safe in Content Manager.");
    // Persist the "dismissed" intent so /api/posts/latest-generate-draft
    // does not re-surface this draft on next visit or refresh. Non-fatal
    // if the network call fails — the local UI is already reset, and the
    // banner may briefly reappear on a subsequent visit until the next
    // dismiss succeeds. We deliberately do NOT block the UI on this.
    if (dismissId) {
      try {
        await api.post(`/posts/${dismissId}/dismiss-resume`);
      } catch (_err) {
        console.warn("Failed to persist dismiss-resume; banner may reappear next visit.");
      }
    }
  };

  const onGenerate = async (e) => {
    e?.preventDefault?.();
    if (!topic.trim()) {
      toast.error("Enter a topic or goal first");
      return;
    }
    // Phase 15: Custom goal — require at least a short string. Otherwise
    // the backend passthrough would just get an empty "Custom goal" and
    // silently fall back to the generic guardrails.
    const resolvedGoal = (goal === CUSTOM_GOAL_VALUE)
      ? (customGoal.trim() || "No specific goal")
      : goal;
    if (goal === CUSTOM_GOAL_VALUE && !customGoal.trim()) {
      toast.error("Type your custom goal — or pick a preset from the list.");
      return;
    }
    if (aiAtLimit) {
      toast.error("You've reached your monthly AI generation limit. Upgrade in Billing to keep generating.");
      return;
    }
    // Resolve the EFFECTIVE tone that will actually be sent to the AI.
    // - "Use my brand tone" -> null (backend falls back to profile.tone)
    // - "Other" -> user-typed custom tone (validated non-empty)
    // - "Random" -> a fresh random pick from RANDOM_TONE_POOL each call
    //   (including on Regenerate, which reuses this same code path)
    // - any other value -> that concrete preset as-is
    let effectiveTone;
    if (tone === "Use my brand tone") {
      effectiveTone = null;
    } else if (tone === CUSTOM_TONE_LABEL) {
      const trimmedCustom = (customTone || "").trim();
      if (!trimmedCustom) {
        toast.error("Type a custom tone (up to " + CUSTOM_TONE_MAX + " characters), or pick one from the list.");
        return;
      }
      effectiveTone = trimmedCustom;
    } else if (tone === RANDOM_TONE_LABEL) {
      const pool = (RANDOM_TONE_POOL && RANDOM_TONE_POOL.length > 0) ? RANDOM_TONE_POOL : ["Friendly"];
      effectiveTone = pool[Math.floor(Math.random() * pool.length)];
    } else {
      effectiveTone = tone;
    }
    setLoading(true);
    try {
      const res = await api.post("/ai/generate-post", {
        topic,
        goal: resolvedGoal,
        platform,
        tone_override: effectiveTone,
        length_override: postLength,
        // Prompt-first UX (Feb 2026): pass through the optional Content
        // Format hint. "auto" lets the backend detect meme/promo/etc.
        // from the natural-language topic; an explicit override always
        // wins. Standard generation still works exactly as before —
        // the backend gates meme-specific behaviour behind meme mode.
        content_format: contentFormat || "auto",
        // When regenerating in the same session, update the existing draft
        // in-place rather than create a new one.
        current_post_id: currentPostId || null,
        // Image source policy — "none"/"upload" never burn an AI image
        // credit; "library" picks from the user's media library; "ai" calls
        // the image generator alongside the caption.
        image_source: imageSource === "upload" ? "none" : imageSource,
        // Phase 15: manual library selection. Only sent when the user
        // is in library mode AND has an explicit selection.
        library_image_id: (imageSource === "library" && libraryPickMode === "manual" && librarySelectedImageId)
          ? librarySelectedImageId
          : null,
      });
      setResult(res.data);
      setEditedCaption(res.data.caption || "");
      setEditedHashtags(res.data.hashtags || []);
      if (res.data?.image_url) {
        setEditedImage(res.data.image_url);
      }
      if (res.data?.post_id) {
        setCurrentPostId(res.data.post_id);
      }
      setResumed(false);
      // Friendlier toasts based on what actually happened with the image.
      if (imageSource === "library" && res.data?.library_image_empty) {
        toast.warning(
          "No images in your library yet — caption generated. Add photos to your library, then regenerate.",
        );
      } else {
        toast.success(currentPostId ? "Regenerated and saved to your draft" : "Post generated and saved as draft");
      }
      refreshSubscription();
    } catch (err) {
      console.error(err);
      // Content safety errors — the global interceptor already surfaced
      // a generic toast; skip a duplicate here (also prevents rendering
      // the machine-readable detail dict).
      if (!isContentPolicyBlockError(err) && !isContentSafetyUnavailableError(err)) {
        toast.error(formatApiError(err, "AI generation failed. Try again."));
      }
      refreshSubscription();
    } finally {
      setLoading(false);
    }
  };

  // ----- Auto-save edits to the persisted draft -----
  // Debounced PATCH so the user never loses caption/hashtag/image edits.
  // No-op until we have a current draft id.
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!currentPostId) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/posts/${currentPostId}`, {
          caption: editedCaption,
          hashtags: editedHashtags,
          image_url: editedImage,
          platform,
          target_platforms: (targetPlatforms && targetPlatforms.length > 0)
            ? targetPlatforms
            : [platform],
        });
      } catch (_e) {
        // Silent — auto-save shouldn't spam the user. Manual Save / Schedule
        // will re-attempt and surface any error.
      }
    }, 900);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [currentPostId, editedCaption, editedHashtags, editedImage, platform, targetPlatforms]);

  // When the user picks "Upload image manually" in the compact
  // Image source tiles, we render the SAME <ImageDropzone> inline
  // directly under the tile grid (see JSX below). Both the inline
  // dropzone and the right-side Image & preview dropzone share the
  // exact same `editedImage` / `setEditedImage` state, so uploading
  // from either updates the other seamlessly.

  const onGenerateImage = async () => {
    if (!editedCaption) {
      toast.error("Generate or write a caption first");
      return;
    }
    if (aiAtLimit) {
      toast.error("You've reached your monthly AI generation limit. Upgrade in Billing to keep generating.");
      return;
    }
    setImageGenerating(true);
    toast.info("Generating image · this may take ~20s");
    try {
      const res = await api.post("/ai/generate-image", {
        prompt: (editedCaption || "").slice(0, 220),
        caption: editedCaption || "",
        topic: topic || "",
        goal: (goal === CUSTOM_GOAL_VALUE ? (customGoal || "") : goal) || "",
        platform: platform || "",
      });
      if (res.data?.image_url) {
        setEditedImage(res.data.image_url);
        toast.success("Image generated");
      } else {
        toast.error("Image returned empty");
      }
      refreshSubscription();
    } catch (err) {
      // Content safety errors — interceptor already toasted a generic
      // message. Draft is preserved (image simply not set on the post).
      if (!isContentPolicyBlockError(err) && !isContentSafetyUnavailableError(err)) {
        toast.error(formatApiError(err, "Image generation failed"));
      }
      refreshSubscription();
    } finally {
      setImageGenerating(false);
    }
  };

  const applyVariant = (text) => {
    setEditedCaption(text);
    toast.info("Variant applied to caption");
  };

  // Save / Schedule now update the existing auto-draft (if any) instead of
  // creating a brand-new post. This guarantees Content Manager never shows
  // a phantom duplicate and the user keeps editing a single living post.
  // saveAs("draft" | "scheduled", scheduledAt?) — patches the currently
  // open Generate-page draft (or POSTs a new one if none exists yet).
  //
  // Draft-banner hygiene: after a successful save/schedule we must clear
  // the "unfinished draft from last time" resume banner if it points at
  // the post we just completed, and (for schedule) reset the editor so
  // the page is genuinely clean for the next interaction. Without this,
  // the banner can linger on stale client state even though the DB is
  // fine.
  const saveAs = async (status, scheduledAt = null) => {
    const payload = {
      caption: editedCaption,
      hashtags: editedHashtags,
      platform,
      // Multi-platform fan-out target list (the multi-select on Generate
      // page). Backend persists this and the publish endpoint fans the
      // post out to every entry. Falls back to `[platform]` when empty so
      // single-platform behavior is preserved.
      target_platforms: (targetPlatforms && targetPlatforms.length > 0)
        ? targetPlatforms
        : [platform],
      image_url: editedImage,
      scheduled_at: scheduledAt,
      status,
    };
    // Explicit "Save as draft" click → tell backend the user treated this
    // draft as done. `/api/posts/latest-generate-draft` will exclude it
    // so the banner does NOT mislead them next visit.
    if (status === "draft") {
      payload.manually_saved = true;
    }
    try {
      const completedPostId = currentPostId; // capture BEFORE we clear it
      if (currentPostId) {
        await api.patch(`/posts/${currentPostId}`, payload);
      } else {
        const res = await api.post("/posts", payload);
        if (res?.data?.post_id) setCurrentPostId(res.data.post_id);
      }
      toast.success(status === "scheduled" ? "Scheduled" : "Saved as draft");
      // Drop the resume banner if it was pointing at the post the user
      // just finished. This fixes the reported "banner still says
      // unfinished after I saved/scheduled" bug — the banner used to be
      // driven by stale in-memory state even after a successful save.
      setPendingDraft((prev) =>
        prev && completedPostId && prev.post_id === completedPostId ? null : prev
      );
      if (status === "scheduled") {
        // Scheduled posts are no longer drafts. Reset editor + resume
        // context so the page is clean for a fresh generation.
        setCurrentPostId(null);
        setResult(null);
        setEditedCaption("");
        setEditedHashtags([]);
        setEditedImage(null);
        setResumed(false);
      }
      return true;
    } catch (err) {
      toast.error(formatApiError(err, status === "scheduled" ? "Could not schedule post" : "Save failed"));
      return false;
    }
  };

  const onSchedule = async (isoString) => {
    if (!isoString) {
      toast.error("Choose a valid future date and time.");
      return;
    }
    setScheduling(true);
    try {
      const ok = await saveAs("scheduled", isoString);
      if (ok) setScheduleOpen(false);
    } finally {
      setScheduling(false);
    }
  };

  const onRegenerateClick = () => {
    // "Regenerate" reuses the same prompt + existing draft. It still costs
    // one AI generation (same rule as elsewhere) but doesn't create a new
    // draft. We pre-warn so users don't accidentally burn quota.
    if (aiAtLimit) {
      toast.error("You've reached your monthly AI generation limit. Upgrade in Billing to keep generating.");
      return;
    }
    onGenerate();
  };

  // --------- Render ---------
  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Generate Posts"
          subtitle="Tell Postmoro your topic. Get a caption, hashtags, variants, and ideas in seconds."
          testidPrefix="generate"
        />

        {/* Usage indicator — always visible on Generate */}
        <div
          className="mb-4 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
          data-testid="generate-usage-indicator"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">AI generations · this month</div>
              <div className="mt-0.5 text-sm">
                <span className="font-mono text-[color:var(--text-1)] tabular-nums" data-testid="generate-ai-usage-line">
                  {aiUnlimited ? `${aiUsed} used` : `${aiUsed} of ${aiLimit} used`}
                </span>
                {!aiUnlimited && aiRemaining !== null ? (
                  <span className="text-[color:var(--text-3)] text-xs"> · {aiRemaining} remaining</span>
                ) : null}
              </div>
              {!aiUnlimited && aiLimit > 0 ? (
                <div className="mt-2 w-40 sm:w-56">
                  <ProgressRail value={aiUsed} max={aiLimit} />
                </div>
              ) : null}
            </div>
          </div>
          <Link
            to="/billing"
            className="text-[color:var(--brand-primary)] hover:underline text-[11px] shrink-0"
            data-testid="generate-usage-billing-link"
          >
            View plan
          </Link>
        </div>

        {/* Brand Learning quick-link */}
        <div className="mb-4">
          <BrandLearningStatusCard testid="generate-brand-learning-status" compact />
        </div>

        {/* AI generation cap banner — only shows when at-limit. */}
        {aiAtLimit ? (
          <div
            className="mb-4 rounded-lg border border-[color:rgba(255,92,108,0.4)] bg-[color:rgba(255,92,108,0.06)] p-4 flex items-start gap-3"
            data-testid="generate-ai-limit-banner"
          >
            <Sparkles className="h-5 w-5 text-[color:var(--danger)] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[color:var(--text-1)]">
                You&apos;ve reached your monthly AI generation limit.
              </div>
              <p className="text-sm text-[color:var(--text-2)] mt-1 leading-relaxed">
                You used <span className="font-mono">{aiUsed}/{aiLimit}</span> AI generations this
                month on the <span className="capitalize">{sub?.effective_plan || sub?.plan || "free"}</span> plan.
                Upgrade to keep generating, or wait until your plan renews.
              </p>
            </div>
            <Link to="/billing" className="shrink-0">
              <Button
                size="sm"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="generate-ai-limit-upgrade"
              >
                Upgrade
              </Button>
            </Link>
          </div>
        ) : null}

        {/* Resume-draft banner — only when a previous auto-saved draft
            exists AND the user hasn't yet picked Continue/Start new. */}
        {pendingDraft && !result ? (
          <div
            className="mb-4 rounded-lg border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.06)] p-4 flex items-start gap-3 flex-wrap"
            data-testid="generate-resume-banner"
          >
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] shadow-[0_4px_12px_rgba(27,168,159,0.35)] shrink-0">
              <FileText className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                You have an unfinished draft from last time
              </div>
              <p className="text-sm text-[color:var(--text-2)] mt-1 leading-relaxed">
                Continue editing the latest generated post, or start a new generation.
                Starting a new generation will use one more AI generation from your monthly limit.
              </p>
              <div className="text-[11px] text-[color:var(--text-3)] mt-2 line-clamp-2">
                {pendingDraft.caption?.slice(0, 200) || "Saved draft"}
              </div>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <Button
                onClick={() => continueDraft(pendingDraft)}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="generate-resume-continue"
              >
                <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
                Continue editing latest draft
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmNewOpen(true)}
                disabled={aiAtLimit}
                title={aiAtLimit ? "Monthly AI generation limit reached — upgrade in Billing to keep generating." : undefined}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="generate-resume-start-new"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Start new generation
              </Button>
            </div>
          </div>
        ) : null}

        {/* Active-session pill when working on a persisted draft */}
        {currentPostId && result ? (
          <div
            className="mb-4 rounded-md border border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.06)] px-3 py-2 text-xs text-[color:var(--text-2)] inline-flex items-center gap-2"
            data-testid="generate-autosave-pill"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--success)]" />
            {resumed ? "Editing your saved draft" : "Auto-saved to your draft"}{" "}
            <span className="text-[10px] text-[color:var(--text-3)] font-mono ml-1">
              {currentPostId}
            </span>
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Form */}
          <GlassCard className="p-6 lg:col-span-4" data-testid="generate-composer-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(34,211,238,0.16)] flex items-center justify-center border border-[color:rgba(27,168,159,0.35)]">
                <Wand2 className="h-4 w-4 text-[color:var(--brand-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Creative Brief</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Tell Postmoro what to make. Every field steers the draft.</div>
              </div>
            </div>
            <form onSubmit={onGenerate} className="space-y-4">
              <div>
                <label className="text-xs text-[color:var(--text-2)]" htmlFor="generate-topic-input">
                  What do you want to create?
                </label>
                <Textarea
                  id="generate-topic-input"
                  value={topic}
                  onChange={(e) => {
                    const raw = e.target.value ?? "";
                    if (raw.length > TOPIC_MAX_LEN) {
                      setTopic(raw.slice(0, TOPIC_MAX_LEN));
                      toast.message(`Prompt is limited to ${TOPIC_MAX_LEN.toLocaleString()} characters.`, {
                        id: "topic-len-cap",
                      });
                    } else {
                      setTopic(raw);
                    }
                  }}
                  maxLength={TOPIC_MAX_LEN}
                  placeholder={
                    "Tell Postmoro what to make.\n\n" +
                    "e.g. Promote our weekend brunch special with a $5 mimosa add-on\n" +
                    "e.g. Share a quick tip our customers always ask about\n" +
                    "e.g. Announce our new online booking on LinkedIn"
                  }
                  data-testid="generate-topic-input"
                  className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] min-h-[140px]"
                />
                <div
                  data-testid="generate-topic-counter"
                  className={
                    "mt-1 text-[10px] text-right tabular-nums " +
                    (topic.length >= TOPIC_MAX_LEN
                      ? "text-[color:var(--danger,#dc2626)] font-semibold"
                      : topic.length >= TOPIC_MAX_LEN * 0.9
                        ? "text-[color:var(--warning,#d97706)]"
                        : "text-[color:var(--text-3)]")
                  }
                  aria-live="polite"
                >
                  {topic.length.toLocaleString()} / {TOPIC_MAX_LEN.toLocaleString()}
                </div>
              </div>
              <div>
                <label
                  className="text-[10px] font-medium uppercase tracking-[0.14em] text-[color:var(--text-3)]"
                  htmlFor="generate-content-format"
                >
                  Content format
                </label>
                <Select value={contentFormat} onValueChange={setContentFormat}>
                  <SelectTrigger
                    id="generate-content-format"
                    className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="generate-content-format"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto" data-testid="generate-content-format-auto">
                      Auto — detect from prompt (recommended)
                    </SelectItem>
                    <SelectItem value="standard" data-testid="generate-content-format-standard">
                      Standard post
                    </SelectItem>
                    <SelectItem value="promotion" data-testid="generate-content-format-promotion">
                      Promotion
                    </SelectItem>
                    <SelectItem value="educational" data-testid="generate-content-format-educational">
                      Educational
                    </SelectItem>
                    <SelectItem value="announcement" data-testid="generate-content-format-announcement">
                      Announcement
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="mt-1 text-[10px] text-[color:var(--text-3)]" data-testid="generate-content-format-hint">
                  Leave on Auto — say "promote…", "announce…", or "teach…" in your prompt and Postmoro figures out the rest.
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.14em] text-[color:var(--text-3)]">Goal</label>
                  <Select value={goal} onValueChange={setGoal}>
                    <SelectTrigger
                      className="mt-1 h-auto min-h-[52px] items-start whitespace-normal rounded-lg px-3 py-2.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] hover:border-[color:rgba(27,168,159,0.35)] focus:ring-[color:rgba(27,168,159,0.55)] data-[state=open]:border-[color:rgba(27,168,159,0.55)] data-[state=open]:bg-[color:rgba(27,168,159,0.06)] [&>span]:line-clamp-none"
                      data-testid="generate-goal"
                    >
                      <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5 text-left pr-2">
                        <span className="text-sm font-medium leading-tight text-[color:var(--text-1)] whitespace-normal break-words">
                          {(POST_GOALS.find((g) => g.value === goal) || {}).label || goal}
                        </span>
                        <span className="text-[11px] leading-tight text-[color:var(--text-3)] whitespace-normal break-words">
                          {(POST_GOALS.find((g) => g.value === goal) || {}).hint || "\u00a0"}
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-[360px]">
                      {POST_GOALS.map((g) => (
                        <SelectItem
                          key={g.value}
                          value={g.value}
                          className="py-2"
                          data-testid={`generate-goal-option-${g.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        >
                          <div className="flex flex-col gap-0.5">
                            <span className="leading-tight">{g.label}</span>
                            {g.hint ? (
                              <span className="text-[10px] text-[color:var(--text-3)] leading-tight">{g.hint}</span>
                            ) : null}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {goal === CUSTOM_GOAL_VALUE ? (
                    <div className="mt-2">
                      <Input
                        value={customGoal}
                        onChange={(e) => setCustomGoal(e.target.value.slice(0, 120))}
                        placeholder="e.g. Get more repeat visits from local families"
                        maxLength={120}
                        data-testid="generate-goal-custom-input"
                        className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm"
                      />
                      <div className="text-[10px] text-[color:var(--text-3)] mt-1 flex justify-between">
                        <span>Your custom goal guides the AI for this post.</span>
                        <span>{customGoal.length}/120</span>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.14em] text-[color:var(--text-3)]">Tone</label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger
                      className="mt-1 h-auto min-h-[52px] items-start whitespace-normal rounded-lg px-3 py-2.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] hover:border-[color:rgba(27,168,159,0.35)] focus:ring-[color:rgba(27,168,159,0.55)] data-[state=open]:border-[color:rgba(27,168,159,0.55)] data-[state=open]:bg-[color:rgba(27,168,159,0.06)] [&>span]:line-clamp-none"
                      data-testid="generate-tone"
                    >
                      <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5 text-left pr-2">
                        <span className="text-sm font-medium leading-tight text-[color:var(--text-1)] whitespace-normal break-words">
                          {tone}
                        </span>
                        <span className="text-[11px] leading-tight text-[color:var(--text-3)] whitespace-normal break-words">
                          {TONE_HINTS[tone] || "\u00a0"}
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-[360px]">
                      {TONES.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          className="py-2"
                          data-testid={`generate-tone-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        >
                          <div className="flex flex-col gap-0.5">
                            <span className="leading-tight">{t}</span>
                            {TONE_HINTS[t] ? (
                              <span className="text-[10px] text-[color:var(--text-3)] leading-tight">{TONE_HINTS[t]}</span>
                            ) : null}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tone === CUSTOM_TONE_LABEL ? (
                    <div className="mt-2" data-testid="generate-tone-custom-wrap">
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-[color:var(--text-2)]">Custom tone</label>
                        <span
                          className="text-[11px] text-[color:var(--text-3)]"
                          data-testid="generate-tone-custom-counter"
                        >
                          {(customTone || "").length}/{CUSTOM_TONE_MAX}
                        </span>
                      </div>
                      <Input
                        value={customTone}
                        onChange={(e) =>
                          setCustomTone(e.target.value.slice(0, CUSTOM_TONE_MAX))
                        }
                        placeholder="e.g. Luxury and refined"
                        maxLength={CUSTOM_TONE_MAX}
                        data-testid="generate-tone-custom-input"
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                  ) : null}
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[color:var(--text-2)]">Post length</label>
                  <div
                    className="mt-1 grid grid-cols-3 gap-2"
                    data-testid="generate-post-length"
                    role="radiogroup"
                    aria-label="Post length"
                  >
                    {POST_LENGTHS.map((k) => {
                      const active = postLength === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          data-testid={`generate-post-length-${k}`}
                          onClick={() => setPostLength(k)}
                          className={
                            "group flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors " +
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgba(27,168,159,0.55)] " +
                            (active
                              ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.12)] text-[color:var(--text-1)]"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:border-[color:rgba(27,168,159,0.35)] hover:bg-[color:rgba(27,168,159,0.06)] hover:text-[color:var(--text-1)]")
                          }
                        >
                          <span className={"text-sm font-medium leading-tight " + (active ? "text-[color:var(--text-1)]" : "")}>
                            {POST_LENGTH_LABELS[k]}
                          </span>
                          <span className="mt-0.5 text-[10px] leading-tight text-[color:var(--text-3)]">
                            {POST_LENGTH_HINTS[k]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[color:var(--text-2)] flex items-center justify-between">
                    <span>Platforms</span>
                    <span className="text-[10px] text-[color:var(--text-3)] uppercase tracking-wider">
                      {targetPlatforms.length} selected
                    </span>
                  </label>
                  {slotCtx && !slotCtx.unlimited && (slotCtx.active_platforms || []).length === 0 ? (
                    <div
                      className="mt-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3 text-xs"
                      data-testid="generate-no-platforms-empty"
                    >
                      <div className="text-sm font-medium text-[color:var(--text-1)] mb-1">
                        Connect a platform to generate your first post
                      </div>
                      <div className="text-[color:var(--text-3)] leading-relaxed mb-2">
                        {slotCtx.unlimited || !slotCtx.platform_limit
                          ? `${(slotCtx.plan || "").replace(/^./, (c) => c.toUpperCase())} includes every supported platform.`
                          : `${(slotCtx.plan || "").replace(/^./, (c) => c.toUpperCase())} includes ${slotCtx.platform_limit} platforms of your choice.`}
                      </div>
                      <Link to="/settings/connections">
                        <Button
                          size="sm"
                          className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                          data-testid="generate-choose-platforms-cta"
                        >
                          Choose platforms
                        </Button>
                      </Link>
                    </div>
                  ) : null}
                  <div className="mt-1 grid grid-cols-2 gap-2" data-testid="generate-platforms">
                    {PLATFORMS.map((p) => {
                      const conn = connFor(p);
                      const comingSoon = !!conn?.coming_soon;
                      const reqReview = !!conn?.awaiting_app_review;
                      const connected = !!conn?.connected;
                      const selected = targetPlatforms.includes(p);
                      // Phase 17: platform-slot gating. Convert label
                      // ("X / Twitter") → canonical key ("x") and check
                      // if it's active on the current plan.
                      const canon = ({
                        Facebook: "facebook",
                        Instagram: "instagram",
                        LinkedIn: "linkedin",
                        "X / Twitter": "x",
                      })[p] || p.toLowerCase();
                      const slotInactive =
                        !!slotCtx &&
                        !slotCtx.unlimited &&
                        !(slotCtx.active_platforms || []).includes(canon);
                      const disabled = comingSoon || slotInactive;
                      const reason = comingSoon
                        ? (p === "LinkedIn" ? "Planned" : "Coming soon")
                        : slotInactive
                        ? `Not active on your ${(slotCtx?.plan || "").replace(/^./, (c) => c.toUpperCase())} plan`
                        : reqReview
                        ? "Requires Meta approval"
                        : !connected
                        ? "Not connected — preview only"
                        : null;
                      return (
                        <button
                          key={p}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            if (disabled) return;
                            const cur = new Set(targetPlatforms);
                            if (cur.has(p)) {
                              if (cur.size === 1) return; // keep at least 1
                              cur.delete(p);
                            } else {
                              cur.add(p);
                            }
                            setTargetPlatforms(Array.from(cur));
                          }}
                          title={reason || undefined}
                          data-testid={`generate-platform-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={
                            "relative flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs text-left transition " +
                            (selected
                              ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                              : disabled
                              ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed opacity-60"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:border-[color:var(--border-strong)]")
                          }
                        >
                          <span className="flex-1 truncate">{p}</span>
                          {selected ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
                          ) : disabled ? (
                            <Lock className="h-3 w-3 text-[color:var(--text-3)]" />
                          ) : !connected ? (
                            <span className="text-[9px] uppercase tracking-wider text-[color:rgb(255,190,110)] truncate">
                              {reqReview ? "Review" : "Not conn."}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-[color:var(--text-3)] mt-1">
                    Caption is shared across platforms. X-version is auto-trimmed to 280 chars in preview.
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="text-xs text-[color:var(--text-2)]">Image source</label>
                  <div
                    className="mt-1 rounded-md border border-[color:rgba(27,168,159,0.25)] bg-[color:rgba(27,168,159,0.05)] p-2.5 text-[11px] leading-snug"
                    data-testid="generate-image-source-recommendation"
                  >
                    <span className="text-[color:var(--text-1)] font-medium">Real business photos usually perform best.</span>{" "}
                    <span className="text-[color:var(--text-3)]">Use your Media Library or upload your own photo for the most authentic result. AI images are available when you need something new.</span>
                  </div>
                  {imageIsRequired ? (
                    <div
                      className="mt-1 mb-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-2.5 text-xs"
                      data-testid="generate-image-required-notice"
                    >
                      <div className="text-[color:var(--text-1)] font-medium">
                        An image is required for {imageRequiredPlatforms.join(", ")}.
                      </div>
                      <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 leading-snug">
                        {requiresImageReason(targetPlatforms) ||
                          "Upload your own image or let Postmoro generate one with AI."}
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-1 grid grid-cols-2 gap-2" data-testid="generate-image-source">
                    {IMAGE_SOURCES.map((src) => {
                      const Icon = src.Icon;
                      const active = imageSource === src.id;
                      // "No image" is forbidden when any selected
                      // platform requires media. We keep the tile in
                      // the DOM for layout stability but disable it +
                      // annotate it so screen-reader users know why.
                      const noImageBlocked = src.id === "none" && imageIsRequired;
                      return (
                        <button
                          key={src.id}
                          type="button"
                          onClick={() => { if (!noImageBlocked) setImageSource(src.id); }}
                          disabled={noImageBlocked}
                          aria-disabled={noImageBlocked}
                          title={noImageBlocked ? `An image is required for ${imageRequiredPlatforms.join(", ")}.` : undefined}
                          data-testid={`generate-image-source-${src.id}`}
                          className={
                            "flex items-start gap-2 rounded-md border p-2.5 text-left text-xs transition " +
                            (noImageBlocked
                              ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)] opacity-50 cursor-not-allowed"
                              : active
                              ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                          }
                        >
                          <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[color:var(--brand-primary)]" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium flex items-center gap-1.5 flex-wrap">
                              {src.label}
                              {src.recommended && src.id === "library" && libraryCount > 0 ? (
                                <span
                                  className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[color:rgba(27,168,159,0.18)] text-[color:var(--brand-primary)] border border-[color:rgba(27,168,159,0.4)]"
                                  data-testid="generate-image-source-recommended-badge"
                                >
                                  Recommended
                                </span>
                              ) : null}
                            </div>
                            <div className="text-[10px] text-[color:var(--text-3)] leading-snug">
                              {noImageBlocked ? "Required — pick an image source" : src.desc}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {imageSource === "upload" ? (
                    <div
                      className="mt-2"
                      data-testid="generate-image-upload-inline"
                    >
                      <ImageDropzone
                        value={editedImage}
                        onChange={setEditedImage}
                        label="Square image"
                        testidPrefix="generate-image-dropzone-inline"
                      />
                      <div className="text-[11px] text-[color:var(--text-3)] mt-1.5 leading-snug">
                        Also visible in the Image &amp; preview panel — uploading here updates both.
                      </div>
                    </div>
                  ) : null}
                  {imageSource === "library" ? (
                    libraryCount === 0 ? (
                      <div
                        className="mt-2 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-2.5 text-xs"
                        data-testid="generate-library-empty-warning"
                      >
                        <div className="text-[color:var(--text-1)] font-medium">
                          Build your content library
                        </div>
                        <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                          Add product photos, team photos, locations, past campaign images, or brand assets so Postmoro can create more authentic posts.
                        </div>
                        <Link to="/media">
                          <Button
                            size="sm"
                            className="mt-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                            data-testid="generate-library-empty-cta"
                          >
                            Add media
                          </Button>
                        </Link>
                      </div>
                    ) : libraryCount === 1 ? (
                      // Single-image auto-select: show a preview + confirmation.
                      <div
                        className="mt-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5 text-xs flex items-center gap-3"
                        data-testid="generate-library-single-preview"
                      >
                        {libraryImages[0]?.image_url ? (
                          <img
                            src={libraryImages[0].image_url}
                            alt="Selected library photo"
                            className="h-12 w-12 rounded object-cover border border-[color:var(--border-subtle)]"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded bg-[color:var(--surface-3)] border border-[color:var(--border-subtle)] flex items-center justify-center">
                            <LibraryIcon className="h-4 w-4 text-[color:var(--text-3)]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[color:var(--text-1)] font-medium">
                            Using your only library photo
                          </div>
                          <div className="text-[10px] text-[color:var(--text-3)] leading-snug truncate">
                            {libraryImages[0]?.description || "No description added"}
                          </div>
                          <Link
                            to="/media"
                            className="text-[10px] text-[color:var(--brand-primary)] hover:underline"
                          >
                            Add more photos →
                          </Link>
                        </div>
                      </div>
                    ) : (
                      // 2+ images: auto-pick vs manual selection toggle + picker button.
                      <div className="mt-2 space-y-2" data-testid="generate-library-picker">
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setLibraryPickMode("auto");
                              setLibrarySelectedImageId(null);
                            }}
                            aria-pressed={libraryPickMode === "auto"}
                            data-testid="generate-library-mode-auto"
                            className={
                              "rounded-md border p-2.5 text-left text-xs transition " +
                              (libraryPickMode === "auto"
                                ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                            }
                          >
                            <div className="flex items-center gap-1.5">
                              <Sparkles className="h-3 w-3 text-[color:var(--brand-primary)]" />
                              <span className="font-medium">Auto-pick best image</span>
                            </div>
                            <div className="text-[10px] text-[color:var(--text-3)] leading-snug mt-0.5">
                              Postmoro scores your photos by tags &amp; description against the topic.
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setLibraryPickerOpen(true)}
                            aria-pressed={libraryPickMode === "manual"}
                            data-testid="generate-library-mode-manual"
                            className={
                              "rounded-md border p-2.5 text-left text-xs transition " +
                              (libraryPickMode === "manual"
                                ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                            }
                          >
                            <div className="flex items-center gap-1.5">
                              <LibraryIcon className="h-3 w-3 text-[color:var(--brand-primary)]" />
                              <span className="font-medium">Choose specific image</span>
                            </div>
                            <div className="text-[10px] text-[color:var(--text-3)] leading-snug mt-0.5">
                              {libraryPickMode === "manual" && librarySelectedImageId
                                ? "1 selected — click to change"
                                : `Pick from ${libraryCount} photos`}
                            </div>
                          </button>
                        </div>
                        {libraryPickMode === "manual" && librarySelectedImageId ? (
                          <div
                            className="flex items-center gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2"
                            data-testid="generate-library-selected-preview"
                          >
                            {(() => {
                              const sel = libraryImages.find((it) => it.media_id === librarySelectedImageId);
                              return sel?.image_url ? (
                                <img
                                  src={sel.image_url}
                                  alt="Selected library photo"
                                  className="h-10 w-10 rounded object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded bg-[color:var(--surface-3)]" />
                              );
                            })()}
                            <div className="flex-1 min-w-0 text-[11px] text-[color:var(--text-2)]">
                              This photo will be used for the generated post.
                            </div>
                            <button
                              type="button"
                              onClick={() => setLibraryPickerOpen(true)}
                              className="text-[11px] text-[color:var(--brand-primary)] hover:underline"
                              data-testid="generate-library-change-selection"
                            >
                              Change
                            </button>
                          </div>
                        ) : null}
                        <div className="text-[10px] text-[color:var(--text-3)]">
                          No AI image credit used.{" "}
                          <Link to="/media" className="text-[color:var(--brand-primary)] hover:underline">
                            Manage library
                          </Link>
                        </div>
                      </div>
                    )
                  ) : imageSource === "ai" ? (
                    <div className="text-[11px] text-[color:var(--text-3)] mt-1">
                      Counts as one extra AI image generation in addition to the caption.
                    </div>
                  ) : null}
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || aiAtLimit || aiWouldExceed}
                title={aiAtLimit
                  ? "Monthly AI generation limit reached — upgrade in Billing to keep generating."
                  : (aiWouldExceed
                    ? `This ${aiUnitsNeeded}-platform generation uses ${aiUnitsNeeded} AI credits but you only have ${aiRemaining} remaining.`
                    : undefined)}
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-posts-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {loading ? "Drafting captions…" : (currentPostId ? "Regenerate" : "Generate")}
              </Button>
              <div className={"text-[11px] text-center leading-relaxed " + aiCounterTone} data-testid="generate-ai-counter">
                {aiCounterLabel}
              </div>
              <div className="text-[11px] text-[color:var(--text-3)] text-center leading-relaxed" data-testid="generate-ai-per-platform-note">
                Each selected platform uses 1 AI generation credit.
              </div>
              {loading ? (
                <AILoadingState
                  variant={currentPostId ? "regenerate" : "generate"}
                  testidPrefix="generate-loading"
                />
              ) : null}
            </form>
          </GlassCard>

          {/* Results */}
          <GlassCard className="p-6 lg:col-span-5" data-testid="generate-results-card">
            {!result ? (
              <div className="h-full flex flex-col items-center justify-center py-6" data-testid="generate-empty-state">
                {/* Premium empty preview — phone frame + shimmer skeleton.
                    No fake customer content; every string is generic. */}
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)] mb-2">
                  Studio preview
                </div>
                <div className="font-display text-lg sm:text-xl font-semibold text-center text-[color:var(--text-1)]">
                  Your Postmoro draft will appear here
                </div>
                <div className="text-xs text-[color:var(--text-3)] mt-1.5 max-w-sm text-center">
                  Add a topic on the left. We&apos;ll write the caption, variants, hashtags, and follow-up ideas — and drop them into a live platform preview.
                </div>

                {/* Phone-frame skeleton */}
                <div
                  aria-hidden="true"
                  className="mt-6 relative w-full max-w-[280px] rounded-[26px] border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-2 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.7)]"
                >
                  <div className="relative rounded-[20px] overflow-hidden bg-[color:var(--surface-2)]">
                    {/* Header row */}
                    <div className="flex items-center gap-2 p-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[color:rgba(27,168,159,0.35)] to-[color:rgba(34,211,238,0.20)] gen-shimmer" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 w-24 rounded gen-shimmer" />
                        <div className="h-2 w-16 rounded gen-shimmer" />
                      </div>
                      <div className="h-2 w-4 rounded gen-shimmer" />
                    </div>
                    {/* Media area */}
                    <div className="relative aspect-square bg-[color:rgba(27,168,159,0.06)] gen-shimmer overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-10 w-10 rounded-full bg-[color:rgba(27,168,159,0.14)] flex items-center justify-center">
                          <Wand2 className="h-4 w-4 text-[color:var(--brand-primary)]" />
                        </div>
                      </div>
                    </div>
                    {/* Actions row */}
                    <div className="flex items-center gap-2 px-3 pt-2.5">
                      <div className="h-3 w-3 rounded gen-shimmer" />
                      <div className="h-3 w-3 rounded gen-shimmer" />
                      <div className="h-3 w-3 rounded gen-shimmer" />
                    </div>
                    {/* Caption skeleton */}
                    <div className="px-3 pb-3 pt-2 space-y-1.5">
                      <div className="h-2 w-11/12 rounded gen-shimmer" />
                      <div className="h-2 w-4/5 rounded gen-shimmer" />
                      <div className="h-2 w-2/3 rounded gen-shimmer" />
                    </div>
                  </div>
                </div>

                <div className="mt-5 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                  Skeleton preview · not real content
                </div>

                {/* One-time shimmer keyframe — scoped by class name. */}
                <style>{`
                  .gen-shimmer {
                    background-image: linear-gradient(90deg,
                      rgba(255,255,255,0.03) 0%,
                      rgba(27,168,159,0.16) 45%,
                      rgba(34,211,238,0.10) 55%,
                      rgba(255,255,255,0.03) 100%);
                    background-size: 200% 100%;
                    background-repeat: no-repeat;
                    animation: gen-shimmer-move 2.4s linear infinite;
                    background-color: rgba(27,168,159,0.06);
                  }
                  @keyframes gen-shimmer-move {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                  @media (prefers-reduced-motion: reduce) {
                    .gen-shimmer { animation: none; }
                  }
                `}</style>
              </div>
            ) : (
              <Tabs defaultValue="caption" className="w-full">
                <TabsList className="bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]">
                  <TabsTrigger value="caption" data-testid="results-tab-caption">Caption</TabsTrigger>
                  <TabsTrigger value="hashtags" data-testid="results-tab-hashtags">Hashtags</TabsTrigger>
                  <TabsTrigger value="variants" data-testid="results-tab-variants">Variants</TabsTrigger>
                  <TabsTrigger value="ideas" data-testid="results-tab-ideas">Ideas</TabsTrigger>
                </TabsList>

                <TabsContent value="caption" className="mt-4">
                  <Textarea
                    value={editedCaption}
                    onChange={(e) => setEditedCaption(e.target.value)}
                    data-testid="ai-result-caption"
                    className="min-h-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
                  />
                  <div className="mt-3 text-xs text-[color:var(--text-3)]"><strong className="text-[color:var(--brand-primary)]">CTA suggestion:</strong> {result.call_to_action}</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => { navigator.clipboard.writeText(editedCaption); toast.success("Copied caption"); }} data-testid="ai-result-caption-copy-button">
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                    <Button variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => saveAs("draft")} data-testid="ai-result-save-draft-button">
                      <Save className="h-4 w-4 mr-1" /> Save draft
                    </Button>
                    <Button className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" onClick={() => setScheduleOpen(true)} data-testid="ai-result-schedule-button">
                      <CalendarPlus className="h-4 w-4 mr-1" /> Schedule post
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                      onClick={onRegenerateClick}
                      disabled={loading || aiAtLimit}
                      title={aiAtLimit ? "Monthly AI generation limit reached" : "Counts as one AI generation"}
                      data-testid="ai-result-regenerate-button"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" /> Regenerate
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="hashtags" className="mt-4">
                  <div className="flex flex-wrap gap-2" data-testid="ai-result-hashtags">
                    {editedHashtags.map((h, i) => (
                      <HashtagChip key={h + i} value={h} />
                    ))}
                  </div>
                  <Button variant="outline" className="mt-4 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => { navigator.clipboard.writeText(editedHashtags.join(" ")); toast.success("Copied all hashtags"); }} data-testid="copy-all-hashtags-button">
                    <Hash className="h-4 w-4 mr-1" /> Copy all
                  </Button>
                </TabsContent>

                <TabsContent value="variants" className="mt-4">
                  {result.caption_variants ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ai-caption-variants-grid">
                      {[
                        { key: "short", label: "Short", hint: "Punchy, scannable" },
                        { key: "medium", label: "Medium", hint: "Everyday post" },
                        { key: "promotional", label: "Promotional", hint: "Reason to act now" },
                        { key: "local", label: "Local / Community", hint: "Talks to neighbors" },
                      ].map((v) => {
                        const text = result.caption_variants?.[v.key] || "";
                        if (!text) return null;
                        return (
                          <div
                            key={v.key}
                            className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4 flex flex-col gap-2"
                            data-testid={`ai-caption-variant-${v.key}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-xs uppercase tracking-wider text-[color:var(--brand-primary)] font-semibold">{v.label}</div>
                              <div className="text-[10px] text-[color:var(--text-3)]">{v.hint}</div>
                            </div>
                            <div className="text-sm text-[color:var(--text-1)] whitespace-pre-line">{text}</div>
                            <div className="flex gap-2 mt-1">
                              <Button
                                size="sm"
                                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                                onClick={() => applyVariant(text)}
                                data-testid={`use-variant-${v.key}`}
                              >
                                Use this
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-1)]"
                                onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}
                                data-testid={`copy-variant-${v.key}`}
                              >
                                Copy
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Accordion type="single" collapsible className="w-full" data-testid="ai-variants-accordion">
                      {(result.alt_captions || []).map((alt, i) => (
                        <AccordionItem key={i} value={"alt-" + i} className="border-[color:var(--border-subtle)]">
                          <AccordionTrigger className="text-left text-[color:var(--text-1)]">Variant {i + 1}</AccordionTrigger>
                          <AccordionContent>
                            <div className="text-sm text-[color:var(--text-2)]">{alt}</div>
                            <Button variant="outline" size="sm" className="mt-3 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => applyVariant(alt)} data-testid={`use-variant-${i}`}>
                              Use this variant
                            </Button>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </TabsContent>

                <TabsContent value="ideas" className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ai-post-ideas-grid">
                    {(result.post_ideas || []).map((idea, i) => (
                      <div key={i} className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4" data-testid="ai-post-idea-card">
                        <div className="text-sm font-semibold text-[color:var(--text-1)]">{idea.title}</div>
                        <div className="text-xs text-[color:var(--text-2)] mt-1">{idea.description}</div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3 border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-1)]"
                          onClick={() => {
                            setTopic(idea.title + " — " + idea.description);
                            toast.info("Loaded idea into the form");
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Use as next prompt
                        </Button>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </GlassCard>

          {/* Image + Live IG preview */}
          <GlassCard className="p-6 lg:col-span-3" data-testid="generate-preview-card">
            <div className="eyebrow">Visual</div>
            <div className="font-display text-lg font-semibold mb-3">Image & preview</div>
            <div className="space-y-3">
              <ImageDropzone value={editedImage} onChange={setEditedImage} label="Square image" testidPrefix="generate-image-dropzone" />
              <Button
                type="button"
                variant="outline"
                onClick={onGenerateImage}
                disabled={imageGenerating}
                className="w-full border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="generate-image-ai-button"
              >
                {imageGenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {imageGenerating ? "Generating image…" : "Generate with AI"}
              </Button>
              {imageGenerating ? (
                <AILoadingState variant="image" testidPrefix="generate-image-loading" />
              ) : null}
              <div className="hairline" />
              <div className="eyebrow">Live preview</div>
              <PostPreviewCard
                post={{
                  caption: editedCaption || "Your caption preview will appear here.",
                  image_url: editedImage,
                  platform,
                  hashtags: editedHashtags || [],
                }}
                pageName={user?.business_name || "Your Business"}
                handle={user?.business_name ? user.business_name.toLowerCase().replace(/\s+/g, "_") : "your_business"}
                platform={platform}
                availability={previewAvailability}
                testidPrefix="generate-preview"
              />
            </div>
          </GlassCard>
        </div>

        <SchedulePostDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          platform={platform}
          targetPlatforms={targetPlatforms && targetPlatforms.length > 0 ? targetPlatforms : [platform]}
          onConfirm={onSchedule}
          loading={scheduling}
          testidPrefix="generate-schedule"
        />

        <ConfirmDialog
          open={confirmNewOpen}
          onOpenChange={setConfirmNewOpen}
          title="Start a new generation?"
          description="Starting a new generation will create a new AI generation and count toward your monthly AI generation limit. Your previous draft is safe in Content Manager — you can return to it anytime."
          confirmLabel="Start new generation"
          cancelLabel="Keep editing"
          destructive={false}
          onConfirm={async () => {
            startNewGeneration();
          }}
          testidPrefix="generate-new-confirm"
        />

        {/* Phase 15: Business Image Library picker modal */}
        <Dialog open={libraryPickerOpen} onOpenChange={setLibraryPickerOpen}>
          <DialogContent
            className="max-w-3xl bg-[color:var(--surface-1)] border-[color:var(--border-subtle)]"
            data-testid="generate-library-picker-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-[color:var(--text-1)]">
                Choose a photo from your Business Image Library
              </DialogTitle>
              <DialogDescription className="text-[color:var(--text-3)]">
                Click a photo to use it for this generation. This never uses an AI image credit.
              </DialogDescription>
            </DialogHeader>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto py-2"
              data-testid="generate-library-picker-grid"
            >
              {libraryImages.length === 0 ? (
                <div className="col-span-full text-center text-sm text-[color:var(--text-3)] py-8">
                  Your library is empty.
                </div>
              ) : (
                libraryImages.map((img) => {
                  const isSel = img.media_id === librarySelectedImageId;
                  return (
                    <button
                      key={img.media_id}
                      type="button"
                      onClick={() => {
                        setLibrarySelectedImageId(img.media_id);
                        setLibraryPickMode("manual");
                        setLibraryPickerOpen(false);
                        toast.success("Photo selected for this generation");
                      }}
                      data-testid={`generate-library-picker-item-${img.media_id}`}
                      aria-pressed={isSel}
                      className={
                        "relative aspect-square rounded-md overflow-hidden border-2 transition group " +
                        (isSel
                          ? "border-[color:var(--brand-primary)] ring-2 ring-[color:rgba(27,168,159,0.35)]"
                          : "border-[color:var(--border-subtle)] hover:border-[color:var(--border-strong)]")
                      }
                    >
                      {img.image_url ? (
                        <img
                          src={img.image_url}
                          alt={img.description || "Library photo"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full bg-[color:var(--surface-3)] flex items-center justify-center">
                          <ImageIconLucide className="h-6 w-6 text-[color:var(--text-3)]" />
                        </div>
                      )}
                      {isSel ? (
                        <div className="absolute top-1 right-1 bg-[color:var(--brand-primary)] rounded-full p-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--bg-0)]" />
                        </div>
                      ) : null}
                      {img.is_favorite ? (
                        <div className="absolute top-1 left-1 bg-[color:rgba(0,0,0,0.55)] rounded px-1 text-[9px] text-white uppercase tracking-wider">
                          ★
                        </div>
                      ) : null}
                      {img.description ? (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                          {img.description}
                        </div>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
              <div className="text-[11px] text-[color:var(--text-3)]">
                {libraryImages.length} photo{libraryImages.length === 1 ? "" : "s"} in your library.
              </div>
              <div className="flex gap-2">
                {librarySelectedImageId ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setLibrarySelectedImageId(null);
                      setLibraryPickMode("auto");
                      setLibraryPickerOpen(false);
                      toast.info("Switched back to auto-pick");
                    }}
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                    data-testid="generate-library-picker-clear"
                  >
                    Clear selection
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  onClick={() => setLibraryPickerOpen(false)}
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                  data-testid="generate-library-picker-close"
                >
                  Close
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
